import { APP_INITIALIZER, NgModule, ErrorHandler } from '@angular/core';
import { HttpErrorResponse, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ShellDialogsModule } from './shared/shell-dialogs.module';

import { APP_BASE_HREF } from '@angular/common';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { AppInterceptorInterceptor } from './app-interceptor.interceptor';
import { FooterComponent } from './components/footer/footer.component';
import { ErrorPageComponent } from './components/error-page/error-page.component';
import { PagenotfoundComponent } from './components/pagenotfound/pagenotfound.component';

import { NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { CustomDateFormatter } from './services/custom-date-formatter.service';
import { Router } from "@angular/router";
import * as Sentry from '@sentry/angular';
import { SentryErrorHandler } from '@sentry/angular';
import { isExtensionAsyncChannelMessage, isBenignPdfJsCleanupMessage } from './http/http-auth-telemetry';

function isAngularHttpNoise(error: unknown): boolean {
  const unwrapped = (error as { ngOriginalError?: unknown })?.ngOriginalError ?? error;
  if (unwrapped instanceof HttpErrorResponse) {
    return unwrapped.status === 0 || unwrapped.status === 401;
  }
  const message = unwrapped instanceof Error ? unwrapped.message : String(unwrapped ?? '');
  return (
    /Http failure response for .+?:\s*0(?:\s+Unknown Error)?/i.test(message) ||
    /Http failure response.*\b401\b/i.test(message) ||
    // PDF.js: in-flight GetPage after viewer/src teardown (upload mode flip, URL swap).
    /transport destroyed/i.test(message) ||
    isBenignPdfJsCleanupMessage(message) ||
    isExtensionAsyncChannelMessage(message)
  );
}

class QrdaSentryErrorHandler extends SentryErrorHandler {
  override handleError(error: unknown): void {
    if (isAngularHttpNoise(error)) {
      return;
    }
    super.handleError(error);
  }
}
import { ApprovePageComponent } from './approve-page/approve-page.component';
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import localeEn from '@angular/common/locales/en';
import { LOCALE_ID } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { QWD_DATETIME_FORMATS, QwdDatetimeAdapter } from './shared/components/datetime-field/qwd-datetime.adapter';
import { RouteReuseStrategy } from '@angular/router';
import { CustomReuseStrategy } from './utils/custom-reuse-strategy';
import { UserIdleConfig } from 'angular-user-idle';
import { EmptyPageComponent } from './components/empty-page/empty-page.component';
import { ProviderTokenComponent } from './components/provider-token/provider-token.component'
import { ConfigService } from './services/config.service';
import { PdfViewerStateService } from './services/pdf-viewer-state.service';
import { BrowserFingerprintService } from './services/browser-fingerprint.service';
import { AuthSessionService } from './services/auth-session.service';

export function initAppBootstrap(
  cfg: ConfigService,
  fingerprint: BrowserFingerprintService,
  authSession: AuthSessionService,
) {
  return () =>
    (async () => {
      await cfg.load();
      await fingerprint.initialize();
      const extEntry =
        typeof sessionStorage !== 'undefined' && sessionStorage.getItem('extEntry') === '1';
      const awaitingExtensionHandoff =
        extEntry &&
        typeof window !== 'undefined' &&
        window.location.href.includes('/sign-extension');
      // Skip cookie probe on guest sign-link (ref/OTP auth) and while parent awaits extension handoff.
      const skipProbe =
        AuthSessionService.isGuestSignLinkBoot() ||
        (awaitingExtensionHandoff && !AuthSessionService.isSecondScreenChild());
      if (!skipProbe) {
        await authSession.probeSession();
      }
    })();
}
export function userIdleFactory(cfg: ConfigService): UserIdleConfig {
  return {
    idle: cfg.configIfReady?.IDLE_TIMEOUT ?? 900,
    timeout: 1,
    ping: 120
  };
}

@NgModule({ declarations: [
        AppComponent,
        FooterComponent,
        PagenotfoundComponent,
        ErrorPageComponent,
        ApprovePageComponent,
        EmptyPageComponent,
        ProviderTokenComponent
    ],
    bootstrap: [AppComponent], imports: [
        BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        FormsModule,
        ReactiveFormsModule,
        MatProgressSpinnerModule,
        ShellDialogsModule], providers: [
        PdfViewerStateService,
        // The datetime dialog is opened by root MatDialog, so this adapter must live here.
        { provide: DateAdapter, useClass: QwdDatetimeAdapter },
        { provide: MAT_DATE_FORMATS, useValue: QWD_DATETIME_FORMATS },
        { provide: MAT_DATE_LOCALE, useExisting: LOCALE_ID },
        // Material 21 defaults usePopover=true; classic overlay keeps select panel width stable with our CSS.
        { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
        {
            provide: APP_BASE_HREF,
            useValue: window.location.pathname.split('/')[1] ? window.location.pathname.split('/')[1] : '/'
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AppInterceptorInterceptor,
            multi: true
        },
        { provide: NgbDateParserFormatter, useClass: CustomDateFormatter },
        {
            provide: LOCALE_ID,
            useFactory: () => {
                const lang = localStorage.getItem('language');
                if (lang === 'fr') {
                    registerLocaleData(localeFr);
                    return 'fr';
                }
                else {
                    registerLocaleData(localeEn);
                    return 'en';
                }
            }
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initAppBootstrap,
            deps: [ConfigService, BrowserFingerprintService, AuthSessionService],
            multi: true,
        },
        {
            provide: UserIdleConfig,
            useFactory: userIdleFactory,
            deps: [ConfigService]
        },
        {
            provide: ErrorHandler,
            useValue: new QrdaSentryErrorHandler({
                showDialog: false,
                logErrors: false,
            }),
        }, {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => () => { },
            deps: [Sentry.TraceService],
            multi: true,
        },
        { provide: RouteReuseStrategy, useClass: CustomReuseStrategy },
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule {

}
